import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useLanguage } from '../context/LanguageContext';
import { useBrand, buildGradientCSS } from '../context/BrandContext';
import { ENTRA_LOGIN_ERROR_KEY } from '../auth/constants';
import { takeSessionEndReason } from '../auth/session';
import { TeamsLoginSsoStart } from '../components/TeamsSsoPopupFlow';
import * as api from '../api/client';
import { REMEMBERED_EMAIL_KEY } from '../auth/constants';

/**
 * Password sign-in for local accounts.
 *
 * Kept as its own component so the Entra path above it is untouched: the two are separate
 * identity sources and one failing must not disturb the other.
 */
function LocalLoginForm({ t, onSignedIn, emailLinkEnabled }) {
  // Prefilled from the last remembered sign-in, and the box starts ticked when there is one
  // - un-ticking it and signing in is how you forget the address again.
  const remembered = (() => {
    try { return localStorage.getItem(REMEMBERED_EMAIL_KEY) || ''; } catch { return ''; }
  })();
  const [email, setEmail] = useState(remembered);
  const [remember, setRemember] = useState(!!remembered);
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [mustChange, setMustChange] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  // Kept apart from `err`: one is "you did something wrong", the other is "here is the next
  // step", and they must not look the same.
  const [notice, setNotice] = useState('');

  /**
   * Ask for a sign-in link instead of typing a password.
   *
   * Uses the address already in the form. No password is read, and the server answers the
   * same thing whether the account exists or not - so this shows one message and stops.
   */
  const requestLink = async () => {
    const address = email.trim();
    if (!address) { setErr(t('auth.emailRequired')); return; }
    setErr(''); setNotice(''); setBusy(true);
    try {
      const res = await api.requestEmailLink(address);
      setNotice(res.message || t('auth.emailLinkSent'));
    } catch (e2) {
      setErr(e2.message || t('auth.signInFailed'));
    } finally {
      setBusy(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    setNotice('');
    setBusy(true);
    try {
      // Remember the address only on a sign-in that actually succeeds, so a typo does not
      // become the thing we helpfully prefill forever.
      const rememberEmail = () => {
        try {
          if (remember) localStorage.setItem(REMEMBERED_EMAIL_KEY, email.trim());
          else localStorage.removeItem(REMEMBERED_EMAIL_KEY);
        } catch { /* private mode: prefilling is a convenience, not a requirement */ }
      };

      if (mustChange) {
        // A provisioned password is single-use by design: the account cannot reach the app
        // until it has been replaced with one only the user knows.
        await api.changeLocalPassword(email, password, newPassword);
        const after = await api.localLogin(email, newPassword, remember);
        rememberEmail();
        onSignedIn(after);
        return;
      }
      const res = await api.localLogin(email, password, remember);
      if (res.mustChangePassword) {
        // NOT an error. The password was correct - the account is simply provisioned with a
        // one-time password and wants a real one. Putting this in the red error box made a
        // successful sign-in look like a failed one, which is exactly how "I reset the
        // password and login still does not work" happens.
        setMustChange(true);
        setNotice(t('auth.mustChangePassword'));
        return;
      }
      rememberEmail();
      onSignedIn(res);
    } catch (e2) {
      setErr(e2.message || t('auth.signInFailed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="local-login" onSubmit={submit}>
      <label>
        {t('auth.email')}
        <input type="email" value={email} autoComplete="username" required
          onChange={(ev) => setEmail(ev.target.value)} />
      </label>
      <label>
        {mustChange ? t('auth.currentPassword') : t('auth.password')}
        <input type="password" value={password} autoComplete="current-password" required
          onChange={(ev) => setPassword(ev.target.value)} />
      </label>
      {mustChange && (
        <label>
          {t('auth.newPassword')}
          {/* minLength mirrors the server rule. The server is the one that enforces it - this
              only saves a round trip. */}
          <input type="password" value={newPassword} autoComplete="new-password" required minLength={12}
            onChange={(ev) => setNewPassword(ev.target.value)} />
        </label>
      )}
      <label className="local-login-remember">
        <input type="checkbox" checked={remember} onChange={(ev) => setRemember(ev.target.checked)} />
        <span>{t('auth.rememberMe')}</span>
      </label>
      {/* Said out loud, because "remember me" usually implies never being asked again and
          here it cannot mean that: the 30-minute inactivity logout still applies to
          privileged client data. */}
      <p className="local-login-hint">{t('auth.rememberMeHint')}</p>
      {notice && <div className="alert alert-warning local-login-error">{notice}</div>}
      {err && <div className="alert alert-error local-login-error">{err}</div>}
      <button type="submit" className="btn btn-primary" disabled={busy}>
        {busy ? t('auth.signingIn') : (mustChange ? t('auth.setPasswordAndSignIn') : t('auth.signIn'))}
      </button>
      {/* Offered only when the server can actually post the letter, and hidden during the
          change-password step - a link cannot substitute for choosing a password. */}
      {emailLinkEnabled && !mustChange && (
        <>
          {/* "Sifremi unuttum" is the phrase people look for, so it is the label - but the
              mechanism behind it is the emailed sign-in link rather than a separate reset
              token system. One thing to keep working instead of two. */}
          <button type="button" className="btn btn-secondary local-login-emaillink"
            onClick={requestLink} disabled={busy}>
            {t('auth.forgotPassword')}
          </button>
          <p className="local-login-hint">{t('auth.forgotPasswordHint')}</p>
        </>
      )}
    </form>
  );
}

/**
 * After signing in with an emailed link: choose a password, or carry on without one.
 *
 * Skippable on purpose. The link already got them in, and refusing to continue until they
 * invent a twelve-character password is how a person ends up pasting one into a notes app.
 */
function SetPasswordAfterLink({ t, session, onDone }) {
  const [pw, setPw] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const save = async (e) => {
    e.preventDefault();
    setErr(''); setBusy(true);
    try {
      await api.setPasswordWithLinkSession(session.token, pw);
      onDone();
    } catch (e2) {
      setErr(e2.message || t('auth.signInFailed'));
      setBusy(false);
    }
  };

  return (
    <form className="local-login link-setpw" onSubmit={save}>
      <div className="alert alert-warning">
        {t('auth.linkSignedInAs')} <b>{session.user?.email}</b>
      </div>
      <label>
        {t('auth.newPassword')}
        <input type="password" value={pw} autoComplete="new-password" required minLength={12}
          onChange={(ev) => setPw(ev.target.value)} />
      </label>
      {err && <div className="alert alert-error local-login-error">{err}</div>}
      <button type="submit" className="btn btn-primary" disabled={busy}>
        {busy ? t('auth.signingIn') : t('auth.setPasswordAndContinue')}
      </button>
      <button type="button" className="btn btn-secondary local-login-emaillink"
        onClick={onDone} disabled={busy}>
        {t('auth.continueWithoutPassword')}
      </button>
    </form>
  );
}

export default function Login() {
  const [role, setRole] = useState('Legal Analyst');
  const [entraError, setEntraError] = useState('');
  const [sessionNotice, setSessionNotice] = useState('');
  const { user, login, roles, isEntraEnabled, teamsReady, signInWithLocalToken } = useAuth();
  // Only offer the password form where the server actually has local accounts configured,
  // rather than showing a form that can only ever fail.
  const [localEnabled, setLocalEnabled] = useState(false);
  const [emailLinkEnabled, setEmailLinkEnabled] = useState(false);
  useEffect(() => {
    api.getAuthMethods()
      .then((m) => { setLocalEnabled(!!m.local); setEmailLinkEnabled(!!m.emailLink); })
      .catch(() => { setLocalEnabled(false); setEmailLinkEnabled(false); });
  }, []);

  /**
   * A sign-in link was clicked: ?elt=<token> is in the URL.
   *
   * Spent immediately and then STRIPPED from the address bar, so the credential does not sit
   * in history or get copied out of the location bar. It is single-use server-side anyway,
   * but leaving it on screen invites someone to share the URL thinking it is just a page.
   */
  const [linkError, setLinkError] = useState('');
  const [linkBusy, setLinkBusy] = useState(false);
  // A session obtained from a link, held here rather than stored, because storing it
  // navigates away - and the whole point of arriving this way is usually to set a password.
  const [linkSession, setLinkSession] = useState(null);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('elt');
    if (!token) return;
    // Strip it before the request, not after: if the request throws, the token must still
    // be gone from the URL rather than left for a refresh to replay.
    params.delete('elt');
    const rest = params.toString();
    window.history.replaceState({}, '', window.location.pathname + (rest ? `?${rest}` : ''));
    setLinkBusy(true);
    api.spendEmailLink(token)
      .then((res) => {
        // Offer the password step when the server says this session may set one; otherwise
        // go straight in. Never block on it - getting in is the point, and someone who only
        // ever signs in by link should not be forced to invent a password.
        if (res.canSetPassword) setLinkSession(res);
        else signInWithLocalToken(res);
      })
      .catch((e) => setLinkError(e.message || t('auth.signInFailed')))
      .finally(() => setLinkBusy(false));
    // signInWithLocalToken and t are stable enough here; this must run exactly once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const { theme, toggleTheme } = useTheme();
  const { lang, setLang, t } = useLanguage();
  const { settings } = useBrand();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) navigate('/', { replace: true });
  }, [user, navigate]);

  // Explain why the user is back here, instead of dropping them on a bare login page.
  useEffect(() => {
    const reason = takeSessionEndReason();
    if (!reason) return;
    setSessionNotice(reason === 'idle' ? t('auth.sessionIdle') : t('auth.sessionExpired'));
  }, [t]);

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(ENTRA_LOGIN_ERROR_KEY);
      if (!raw) return;
      sessionStorage.removeItem(ENTRA_LOGIN_ERROR_KEY);
      const o = JSON.parse(raw);
      const detail = [o.code, o.message].filter(Boolean).join(' – ');
      setEntraError(detail ? `${t('auth.entraFailed')} (${detail})` : t('auth.entraFailed'));
    } catch {
      sessionStorage.removeItem(ENTRA_LOGIN_ERROR_KEY);
    }
  }, [t]);

  /**
   * Does this person sign in with a password?
   *
   * The remembered address is the only durable trace a password user leaves on a device -
   * it is written when they tick "Beni hatırla" and survives sign-out, unlike any token.
   * When it is there, lead with the password form instead of burying it under a Microsoft
   * button and a divider: on a laptop with the session notice showing, "Giriş yap" sits
   * below the fold, which is how this was reported as "it only offers Microsoft".
   *
   * Only the ORDER changes. Both methods stay available to everyone - guessing wrong must
   * cost a scroll, never an option.
   */
  const prefersPassword = (() => {
    try { return !!localStorage.getItem(REMEMBERED_EMAIL_KEY); } catch { return false; }
  })();

  const isInTeams = (() => { try { return window.self !== window.top; } catch { return true; } })();
  const showTeamsSpinner = isEntraEnabled && isInTeams && !teamsReady && !user;

  const loginBg = useMemo(() => {
    const mode = settings[theme] || settings.dark;
    const grad = typeof mode.bgGradient === 'object'
      ? buildGradientCSS(mode.bgGradient)
      : (mode.bgGradient || '');
    if (grad) return { background: grad, backgroundAttachment: 'fixed' };
    if (mode.bg) return { background: mode.bg, backgroundAttachment: 'fixed' };
    return { backgroundAttachment: 'fixed' };
  }, [settings, theme]);

  const handleMicrosoft = async (e) => {
    e.preventDefault();
    setEntraError('');
    try {
      await login();
    } catch (err) {
      console.error(err);
      const detail = [err.errorCode || err.code, err.errorMessage || err.message].filter(Boolean).join(' – ');
      setEntraError(detail ? `${t('auth.entraFailed')} (${detail})` : t('auth.entraFailed'));
    }
  };

  if (showTeamsSpinner) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: 'var(--bg)' }}>
        <div style={{ textAlign: 'center', color: 'var(--text-muted)' }}>
          <div className="spinner" style={{ margin: '0 auto 1rem' }} />
          <p style={{ fontSize: '0.95rem' }}>Signing in with Teams…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="login-page" style={loginBg}>
      {isEntraEnabled ? <TeamsLoginSsoStart /> : null}
      <div className="login-top-actions">
        <div className="login-lang-group">
          <button
            type="button"
            className={`lang-btn ${lang === 'tr' ? 'active' : ''}`}
            onClick={() => setLang('tr')}
            title="Türkçe"
          >
            TR
          </button>
          <button
            type="button"
            className={`lang-btn ${lang === 'en' ? 'active' : ''}`}
            onClick={() => setLang('en')}
            title="English"
          >
            EN
          </button>
        </div>
        <button
          type="button"
          className="theme-btn login-theme-btn"
          onClick={toggleTheme}
          title={theme === 'dark' ? 'Light mode' : 'Dark mode'}
          aria-label="Toggle theme"
        >
          {theme === 'dark' ? (
            <svg className="theme-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <circle cx="12" cy="12" r="5" />
              <path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" />
            </svg>
          ) : (
            <svg className="theme-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
            </svg>
          )}
        </button>
      </div>
      <div className="login-card">
        <h1 className="landing-header-logo">
          <span className="landing-logo-text-primary">B-Agent</span>{' '}
          <span className="landing-logo-text-gradient">Law</span>
        </h1>
        <p className="login-subtitle">
          {/* Naming only Microsoft when a password form is right there told half our users
              they had come to the wrong place. Say what is actually on offer. */}
          {isEntraEnabled && localEnabled
            ? t('auth.bothSubtitle')
            : isEntraEnabled ? t('auth.entraSubtitleMicrosoft') : t('auth.loginSubtitle')}
        </p>
        {sessionNotice && (
          <div className="alert alert-warning" style={{ marginBottom: '1rem', textAlign: 'left', fontSize: '0.9rem' }}>
            {sessionNotice}
          </div>
        )}
        {/* The two identity sources are rendered independently, because they are
            independent: a deployment can offer both (a tenant colleague and an external
            lawyer signing into the same app), either one alone, or - in dev with nothing
            configured - neither, which is the only case that falls back to the role picker.
            Keying the password form off `isEntraEnabled` was wrong: with Entra switched off
            and local accounts configured, it rendered the DEV role picker and no way to
            enter a password at all. */}
        {isEntraEnabled && !prefersPassword && (
          <>
            <button type="button" className="btn btn-primary btn-entra" onClick={handleMicrosoft}>
              {t('auth.signInMicrosoft')}
            </button>
            {entraError && (
              <div className="alert alert-error" style={{ marginTop: '1rem', textAlign: 'left', fontSize: '0.9rem' }}>
                {entraError}
              </div>
            )}
          </>
        )}

        {linkBusy && <div className="alert alert-warning">{t('auth.emailLinkSigningIn')}</div>}
        {linkError && <div className="alert alert-error">{linkError}</div>}

        {linkSession && (
          <SetPasswordAfterLink
            t={t}
            session={linkSession}
            onDone={() => signInWithLocalToken(linkSession)}
          />
        )}

        {isEntraEnabled && localEnabled && !prefersPassword && (
          <div className="login-divider"><span>{t('auth.or')}</span></div>
        )}

        {localEnabled && (
          <LocalLoginForm t={t} onSignedIn={signInWithLocalToken} emailLinkEnabled={emailLinkEnabled} />
        )}

        {/* The same two blocks, the other way round, for someone who signs in with a
            password. Microsoft stays one click away rather than first. */}
        {isEntraEnabled && localEnabled && prefersPassword && (
          <div className="login-divider"><span>{t('auth.or')}</span></div>
        )}

        {isEntraEnabled && prefersPassword && (
          <>
            <button type="button" className="btn btn-secondary btn-entra" onClick={handleMicrosoft}>
              {t('auth.signInMicrosoft')}
            </button>
            {entraError && (
              <div className="alert alert-error" style={{ marginTop: '1rem', textAlign: 'left', fontSize: '0.9rem' }}>
                {entraError}
              </div>
            )}
          </>
        )}

        {!isEntraEnabled && !localEnabled && (
          <form onSubmit={(e) => { e.preventDefault(); login(role); }}>
            <label>
              {t('auth.role')}
              <select value={role} onChange={(e) => setRole(e.target.value)}>
                {roles.map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
            </label>
            <button type="submit" className="btn btn-primary">{t('auth.signIn')}</button>
          </form>
        )}
      </div>
    </div>
  );
}
